import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import LoadingSpinner from '../components/LoadingSpinner';
import { useToast } from '../hooks/use-toast';
import { 
  Calendar, MapPin, User, Car, DollarSign, 
  CheckCircle, XCircle, Clock, Phone, Mail, MessageCircle, FileText, Share2 
} from 'lucide-react';
import ReviewSection from '../components/ReviewSection';

const BookingDetails = () => {
  const { bookingId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [booking, setBooking] = useState(null);
  const [car, setCar] = useState(null);
  const [renter, setRenter] = useState(null);
  const [owner, setOwner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (!user) return; // wait for AuthContext
    fetchBookingDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, user]);

  const fetchBookingDetails = async () => {
    try {
      const { bookingsAPI, carsAPI, usersAPI } = await import('../api');
      
      // Fetch booking first
      const bookingRes = await bookingsAPI.getById(bookingId);
      const bookingData = bookingRes.data;
      setBooking(bookingData);

      // Fetch car details with error handling
      try {
        const carRes = await carsAPI.getById(bookingData.car_id);
        setCar(carRes.data);
      } catch (carError) {
        console.warn('Car not found, using mock data:', carError);
        setCar({
          id: bookingData.car_id,
          name: bookingData.car_name || 'Auto no disponible',
          location: bookingData.location || 'N/A',
          price_per_day: bookingData.price_per_day || 0,
          transmission: 'automatic',
          seats: 5,
          car_type: 'sedan',
          images: []
        });
      }

      // Fetch renter details if user is mobilitier/admin
      if (user.role === 'mobilitier' || user.role === 'admin') {
        try {
          const renterRes = await usersAPI.getById(bookingData.user_id);
          setRenter(renterRes.data);
        } catch (userError) {
          console.warn('Renter not found, using mock data:', userError);
          setRenter({
            id: bookingData.user_id,
            name: bookingData.user_name || 'Usuario',
            email: 'user@example.com',
            phone: 'N/A'
          });
        }
      }

      // Fetch owner details if user is the renter
      if (user.role === 'user') {
        try {
          const ownerRes = await usersAPI.getById(bookingData.mobilitier_id);
          setOwner(ownerRes.data);
        } catch (ownerError) {
          console.warn('Owner not found, using mock data:', ownerError);
          setOwner({
            id: bookingData.mobilitier_id,
            name: bookingData.owner_name || 'Mobilitier',
            email: 'mobilitier@example.com',
            phone: 'N/A'
          });
        }
      }
    } catch (error) {
      console.error('Error fetching booking:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo cargar la reserva',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      const { bookingsAPI } = await import('../api');
      await bookingsAPI.approve(bookingId);
      
      toast({
        title: '¡Aprobada!',
        description: 'La reserva ha sido aprobada. Se notificó al usuario.'
      });
      
      fetchBookingDetails();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Error al aprobar',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    setActionLoading(true);
    try {
      const { bookingsAPI } = await import('../api');
      await bookingsAPI.reject(bookingId, { reason: 'No disponible' });
      
      toast({
        title: 'Rechazada',
        description: 'La reserva ha sido rechazada'
      });
      
      fetchBookingDetails();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Error al rechazar',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadAgreement = async () => {
    setActionLoading(true);
    try {
      const { bookingsAPI } = await import('../api');
      const res = await bookingsAPI.downloadAgreement(bookingId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mobility-acuerdo-${bookingId.slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast({
        title: 'Acuerdo descargado',
        description: 'El PDF del acuerdo de renta se descargó correctamente.'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo generar el PDF',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleShareAgreementWhatsApp = async () => {
    setActionLoading(true);
    try {
      const { bookingsAPI } = await import('../api');
      const res = await bookingsAPI.createAgreementShareLink(bookingId);
      const link = res.data.url;

      // Pick counterparty phone — strip non-digits for wa.me
      const counterparty = isRenter ? owner : renter;
      const rawPhone = counterparty?.phone || '';
      const digits = rawPhone.replace(/\D/g, '');
      // If less than 10 digits, fall back to "no number" wa.me share (user picks contact)
      const waNumber = digits.length >= 10 ? digits : '';

      const carName = car?.name || 'vehículo';
      const msg =
        `Hola 👋, te comparto el Acuerdo de Renta digital de Mobility para "${carName}" ` +
        `(reserva #${bookingId.slice(-8)}).\n\n` +
        `📄 Link (válido 24h): ${link}\n\n` +
        `Mobility opera como marketplace neutral. Los términos completos están en mobility-car-rental.com/terms.`;

      const waUrl = waNumber
        ? `https://wa.me/${waNumber}?text=${encodeURIComponent(msg)}`
        : `https://wa.me/?text=${encodeURIComponent(msg)}`;

      window.open(waUrl, '_blank', 'noopener,noreferrer');
      toast({
        title: 'Link de 24h generado',
        description: 'Se abrió WhatsApp con el acuerdo prefirmado.'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo generar el link',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      active: 'bg-blue-100 text-blue-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800',
      rejected: 'bg-red-100 text-red-800'
    };

    const labels = {
      pending: 'Pendiente',
      approved: 'Aprobada',
      active: 'Activa',
      completed: 'Completada',
      cancelled: 'Cancelada',
      rejected: 'Rechazada'
    };

    return (
      <Badge className={styles[status] || 'bg-gray-100 text-gray-800'}>
        {labels[status] || status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Reserva no encontrada</h2>
          <Button onClick={() => navigate(-1)} className="mt-4">Volver</Button>
        </div>
      </div>
    );
  }

  const isMobilitier = user.id === booking.mobilitier_id;
  const isRenter = user.id === booking.user_id;
  const canApprove = isMobilitier && booking.status === 'pending';

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6 flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Reserva #{booking.id.slice(-8)}</h1>
            <p className="text-gray-600 mt-1">
              Creada el {new Date(booking.created_at).toLocaleDateString()}
            </p>
          </div>
          {getStatusBadge(booking.status)}
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-6">
            {/* Car Info */}
            {car && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Car className="w-5 h-5" />
                    Vehículo
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-4">
                    {car.images && car.images[0] && (
                      <img
                        src={car.images[0]}
                        alt={car.name}
                        className="w-32 h-24 object-cover rounded-lg"
                      />
                    )}
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{car.name}</h3>
                      <div className="flex items-center gap-2 text-gray-600 mt-1">
                        <MapPin className="w-4 h-4" />
                        <span className="text-sm">{car.location}</span>
                      </div>
                      <div className="flex gap-4 mt-2 text-sm text-gray-600">
                        <span>{car.transmission === 'automatic' ? 'Automático' : 'Manual'}</span>
                        {(car.seats || car.capacity) && (
                          <>
                            <span>•</span>
                            <span>{car.seats || car.capacity} asientos</span>
                          </>
                        )}
                        {(car.car_type || car.type) && (
                          <>
                            <span>•</span>
                            <span>{car.car_type || car.type}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Booking Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Detalles de la Reserva
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Inicio</p>
                    <p className="font-semibold">
                      {new Date(booking.start_date).toLocaleDateString('es-MX', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Fin</p>
                    <p className="font-semibold">
                      {new Date(booking.end_date).toLocaleDateString('es-MX', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600">Días totales</span>
                    <span className="font-semibold">{booking.days || 1}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600">Precio por día</span>
                    <span className="font-semibold">${car?.price_per_day || 0} MXN</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
                    <span>Total</span>
                    <span className="text-blue-600">${booking.total_price} MXN</span>
                  </div>
                  {isMobilitier && (
                    <div className="flex justify-between text-green-600 mt-2">
                      <span>Tus ganancias (80%)</span>
                      <span className="font-bold">${booking.owner_earnings || (booking.total_price * 0.8).toFixed(0)} MXN</span>
                    </div>
                  )}
                </div>

                {booking.stripe_payment_intent_id && (
                  <div className="bg-green-50 p-3 rounded-lg">
                    <p className="text-sm text-green-800">
                      <CheckCircle className="w-4 h-4 inline mr-1" />
                      Pago autorizado (Stripe)
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Check-in/out info */}
            {(booking.status === 'active' || booking.status === 'completed') && (
              <Card>
                <CardHeader>
                  <CardTitle>Check-In / Check-Out</CardTitle>
                </CardHeader>
                <CardContent>
                  {booking.check_in ? (
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-1" />
                        <div>
                          <p className="font-semibold">Check-In Completado</p>
                          <p className="text-sm text-gray-600">
                            {new Date(booking.check_in.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      
                      {booking.check_out && (
                        <div className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-1" />
                          <div>
                            <p className="font-semibold">Check-Out Completado</p>
                            <p className="text-sm text-gray-600">
                              {new Date(booking.check_out.timestamp).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-gray-600 flex items-center gap-2">
                      <Clock className="w-5 h-5" />
                      <span>Pendiente de check-in</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            {/* Review section (only for renter on completed booking) */}
            {isRenter && booking.status === 'completed' && (
              <ReviewSection booking={booking} />
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Info */}
            {(renter || owner) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    {isMobilitier ? 'Información del Usuario' : 'Información del Mobilitier'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {isMobilitier && renter && (
                    <>
                      <div>
                        <p className="text-sm text-gray-600">Nombre</p>
                        <p className="font-semibold">{renter.name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Email</p>
                        <p className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4" />
                          {renter.email}
                        </p>
                      </div>
                      {renter.phone && (
                        <div>
                          <p className="text-sm text-gray-600">Teléfono</p>
                          <p className="flex items-center gap-2 text-sm">
                            <Phone className="w-4 h-4" />
                            {renter.phone}
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {isRenter && owner && (
                    <>
                      <div>
                        <p className="text-sm text-gray-600">Mobilitier</p>
                        <p className="font-semibold">{owner.name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Email</p>
                        <p className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4" />
                          {owner.email}
                        </p>
                      </div>
                      {owner.phone && (
                        <div>
                          <p className="text-sm text-gray-600">Teléfono</p>
                          <p className="flex items-center gap-2 text-sm">
                            <Phone className="w-4 h-4" />
                            {owner.phone}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Actions */}
            {canApprove && (
              <Card>
                <CardHeader>
                  <CardTitle>Acciones</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={handleApprove}
                    disabled={actionLoading}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Aprobar Reserva
                  </Button>
                  <Button
                    className="w-full"
                    variant="destructive"
                    onClick={handleReject}
                    disabled={actionLoading}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Rechazar
                  </Button>
                </CardContent>
              </Card>
            )}

            {isMobilitier && booking.status === 'confirmed' && !booking.check_in && (
              <Button
                className="w-full bg-[#000000] hover:bg-ink-700"
                onClick={() => navigate(`/checkin/${bookingId}`)}
              >
                Iniciar Check-In
              </Button>
            )}

            {isMobilitier && booking.status === 'active' && booking.check_in && !booking.check_out && (
              <Button
                className="w-full bg-green-600 hover:bg-green-700"
                onClick={() => navigate(`/checkout/${bookingId}`)}
              >
                Iniciar Check-Out
              </Button>
            )}

            {/* Chat shortcut - always visible if there's a counterparty */}
            {(isRenter || isMobilitier) && booking.status !== 'pending' && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate(`/messages?booking=${bookingId}`)}
                data-testid="open-chat-btn"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Abrir chat
              </Button>
            )}

            {/* Digital Rental Agreement PDF */}
            {(isRenter || isMobilitier || user.role === 'admin') &&
              ['confirmed', 'active', 'completed', 'dispute_pending'].includes(booking.status) && (
              <>
                <Button
                  variant="outline"
                  className="w-full border-lime bg-lime/10 hover:bg-lime hover:text-ink text-ink font-comfortaa font-bold"
                  onClick={handleDownloadAgreement}
                  disabled={actionLoading}
                  data-testid="download-agreement-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Descargar Acuerdo (PDF)
                </Button>

                {(isRenter || isMobilitier) && (
                  <Button
                    variant="outline"
                    className="w-full border-green-600 bg-green-50 hover:bg-green-600 hover:text-white text-green-700 font-comfortaa font-bold"
                    onClick={handleShareAgreementWhatsApp}
                    disabled={actionLoading}
                    data-testid="share-agreement-whatsapp-btn"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Compartir por WhatsApp (24h)
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        <div className="mt-6">
          <Button variant="outline" onClick={() => navigate(-1)}>
            Volver
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BookingDetails;
